import React from 'react';
import './GettingStarted.css';
import Icon from './Icon';
import { countScoped, hasText } from './setupProgress';

// Agent onboarding checklist. Each step auto-detects whether it's done from real
// data (contacts/properties in the agent's scope, signature + publish flags on the
// account) and deep-links to the screen where it's completed. Reached from the
// Profile → Getting Started button and as the first-login landing.

function GettingStarted({ agent = {}, scope, onNavigate }) {
  const contactCount = countScoped('ira_contacts', scope);
  const propCount = countScoped('ira_properties', scope);
  const hasSignature = hasText(agent.signature);
  const published = !!agent.publicProfile;

  const steps = [
    {
      key: 'contacts', done: contactCount > 0, go: 'contacts',
      title: 'Import your contacts',
      desc: 'Bring your past clients and leads in so iBetterAgent can keep you in touch automatically.',
      cta: contactCount > 0 ? `${contactCount} imported` : 'Import contacts',
    },
    {
      key: 'properties', done: propCount > 0, go: 'properties',
      title: 'Import your past sales',
      desc: 'Add your listing- and buyer-side closings — this powers home-anniversary reminders and your profile track record.',
      cta: propCount > 0 ? `${propCount} imported` : 'Import sales',
    },
    {
      key: 'profile', done: published, go: 'profile',
      title: 'Publish your public profile',
      desc: 'A shareable page clients can view without logging in — your photo, bio, contact info, and sold listings. Publish it first so you can drop the link into your signature next.',
      cta: published ? 'Published' : 'Set up profile',
    },
    {
      key: 'signature', done: hasSignature, go: 'profile',
      title: 'Add your email signature (with your profile link)',
      desc: "It's added to every reminder and message you send. Use “Insert my profile link” so clients can click through to your published profile — every click is tracked and boosts that contact's engagement score, so you can see who's paying attention.",
      cta: hasSignature ? 'Signature set' : 'Set signature',
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="gs">
      <div className="gs-head">
        <h2>Getting Started</h2>
        <p>
          {allDone
            ? "You're all set — iBetterAgent will keep you top-of-mind automatically. Revisit this page anytime."
            : `A few quick steps to get iBetterAgent working for you. ${doneCount} of ${steps.length} done.`}
        </p>
      </div>

      <div className="gs-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="gs-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <ul className="gs-list">
        {steps.map((s, i) => (
          <li key={s.key} className={`gs-item ${s.done ? 'gs-done' : ''}`}>
            <span className="gs-mark">
              {s.done ? <Icon name="check-circle" size={24} /> : <span className="gs-num">{i + 1}</span>}
            </span>
            <div className="gs-body">
              <div className="gs-title">{s.title}</div>
              <div className="gs-desc">{s.desc}</div>
            </div>
            <button type="button" className={`gs-cta ${s.done ? 'gs-cta-done' : ''}`} onClick={() => onNavigate && onNavigate(s.go)}>
              {s.cta} <Icon name="chevron-right" size={15} />
            </button>
          </li>
        ))}
      </ul>

      {allDone && (
        <div className="gs-celebrate">🎉 Fully set up! Your reminders now send on schedule, and clicks on your profile link show up as engagement on your Contacts.</div>
      )}
    </div>
  );
}

export default GettingStarted;
